import { useState, useMemo } from 'react'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import { supabase } from '@/lib/supabase'
import type { MovimientoCaja, ComprobanteMovimiento, Factura, Cliente, TipoComprobante } from '@/lib/types'
import { formatCurrency, formatDate, r2 } from '@/lib/utils'
import { descargarCsv, type CsvColumn } from '@/lib/csv'
import { IconArrowDown, IconArrowUp, IconDownload, IconEdit, IconFile, IconFileText, IconImage, IconList, IconPaperclip, IconPlus, IconReceipt, IconRefresh, IconSearch, IconTrash, IconX } from '@/components/icons/Icons'
import { KpiRow, KpiStrip, PageHeader, Pagination, StatCard } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'

const emptyMov: Omit<MovimientoCaja, 'id' | 'created_at'> = {
  tipo: 'egreso' as const, categoria: '', concepto: null as string | null, descripcion: '', monto: 0, moneda: 'PEN',
  fecha: new Date().toISOString().slice(0, 10), factura_id: null,
  pago_programado_id: null, cliente_id: null, comprobantes: [] as ComprobanteMovimiento[], notas: null as string | null,
  base_imponible: null as number | null, igv: null as number | null,
  serie_comprobante: null as string | null, numero_comprobante: null as string | null,
  tipo_comprobante: null as TipoComprobante | null,
  tiene_detraccion: false, porcentaje_detraccion: null as number | null,
  monto_detraccion: null as number | null,
}

const TIPO_COMPROBANTE_LABELS: Record<string, string> = {
  factura: 'Factura',
  boleta: 'Boleta',
  recibo: 'Recibo por Honorarios',
  nota_credito: 'Nota de Crédito',
  otro: 'Otro',
}

/* Icono por tipo de comprobante. Antes eran emojis: se veian distinto en cada
   sistema operativo y no heredaban el color del texto. */
const TipoComprobanteIcon = ({ tipo }: { tipo?: string | null }) => {
  const size = 12
  if (tipo === 'boleta') return <IconReceipt size={size} />
  if (tipo === 'recibo') return <IconFileText size={size} />
  if (tipo === 'nota_credito') return <IconRefresh size={size} />
  if (tipo === 'otro') return <IconList size={size} />
  return <IconFile size={size} />
}

const CRUMBS = [{ label: 'Contabilidad', to: '/contabilidad' }, { label: 'Flujo de Caja' }]

export default function FlujoCaja() {
  const { data: items = [], isLoading: loading } = useCachedQuery<MovimientoCaja>('movimientos_caja')
  const { addMut, updateMut, removeMut } = useCachedMutations('movimientos_caja')
  const { data: facturas = [] } = useCachedQuery<Factura>('facturas')
  const { data: clientes = [] } = useCachedQuery<Cliente>('clientes')

  const [modal, setModal] = useState<'create' | 'edit' | null>(null)
  const [form, setForm] = useState(emptyMov)
  const [editId, setEditId] = useState<number | null>(null)
  const [del, setDel] = useState<MovimientoCaja | null>(null)
  const [search, setSearch] = useState('')
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'ingreso' | 'egreso'>('todos')
  const [filtroYear, setFiltroYear] = useState<string>(String(new Date().getFullYear()))
  const [filtroMonth, setFiltroMonth] = useState<string>('todos')
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todos')
  const [uploading, setUploading] = useState(false)


  /* ─── Parse YYYY-MM-DD without timezone offset ─── */
  const parseDateParts = (dateStr: string): [number, number, number] => {
    const [y, m, d] = dateStr.split('-').map(Number)
    return [y, m, d]
  }

  /* ─── Años disponibles ─── */
  const availableYears = useMemo(() => {
    const years = new Set(items.map(m => parseDateParts(m.fecha)[0]))
    return Array.from(years).sort((a, b) => b - a)
  }, [items])

  const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

  /* ─── Categorías existentes para datalist ─── */
  const categorias = useMemo(() => {
    const set = new Set(items.map(m => m.categoria).filter(Boolean))
    return Array.from(set).sort()
  }, [items])

  /* ─── Filtrar ─── */
  const filtered = useMemo(() => {
    let result = items
    if (filtroYear !== 'todos') result = result.filter(m => parseDateParts(m.fecha)[0] === Number(filtroYear))
    if (filtroMonth !== 'todos') result = result.filter(m => parseDateParts(m.fecha)[1] === Number(filtroMonth))
    if (filtroTipo !== 'todos') result = result.filter(m => m.tipo === filtroTipo)
    if (filtroCategoria !== 'todos') result = result.filter(m => (m.categoria || '') === filtroCategoria)
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(m =>
        m.descripcion?.toLowerCase().includes(q) ||
        m.categoria?.toLowerCase().includes(q) ||
        m.notas?.toLowerCase().includes(q) ||
        m.serie_comprobante?.toLowerCase().includes(q) ||
        m.numero_comprobante?.toLowerCase().includes(q)
      )
    }
    return result.sort((a, b) => b.fecha.localeCompare(a.fecha))
  }, [items, filtroTipo, filtroYear, filtroMonth, filtroCategoria, search])

  // Reset page when filters change
  const { page, setPage, totalPages, paginated: paginatedItems, pageSize, total } =
    usePagination(filtered, 15, `${search}|${filtroYear}|${filtroMonth}|${filtroTipo}|${filtroCategoria}`)

  const hayFiltros = !!search || filtroYear !== 'todos' || filtroMonth !== 'todos'
    || filtroTipo !== 'todos' || filtroCategoria !== 'todos'
  const limpiarFiltros = () => {
    setSearch(''); setFiltroYear('todos'); setFiltroMonth('todos')
    setFiltroTipo('todos'); setFiltroCategoria('todos')
  }

  /* ─── Totales filtrados (separados por moneda, con IGV) ─── */
  const totals = useMemo(() => {
    const byMoneda = (moneda: string) => {
      const movs = filtered.filter(m => m.moneda === moneda)
      const ingresos = movs.filter(m => m.tipo === 'ingreso')
      const egresos = movs.filter(m => m.tipo === 'egreso')
      const ing = r2(ingresos.reduce((s, m) => s + m.monto, 0))
      const egr = r2(egresos.reduce((s, m) => s + m.monto, 0))
      const base_ing = r2(ingresos.reduce((s, m) => s + (m.base_imponible ?? m.monto), 0))
      const igv_ing = r2(ingresos.reduce((s, m) => s + (m.igv ?? 0), 0))
      const base_egr = r2(egresos.reduce((s, m) => s + (m.base_imponible ?? m.monto), 0))
      const igv_egr = r2(egresos.reduce((s, m) => s + (m.igv ?? 0), 0))
      return { ingresos: ing, egresos: egr, saldo: r2(ing - egr), base_ing, igv_ing, base_egr, igv_egr }
    }
    return { pen: byMoneda('PEN'), usd: byMoneda('USD') }
  }, [filtered])

  /* ─── Auto-cálculo tributario ─── */
  const handleBaseChange = (val: number) => {
    const base = r2(val)
    const igvCalc = r2(base * 0.18)
    setForm(f => ({ ...f, base_imponible: base, igv: igvCalc, monto: r2(base + igvCalc) }))
  }

  const handleIgvChange = (val: number) => {
    const igv = r2(val)
    const base = form.base_imponible ?? 0
    setForm(f => ({ ...f, igv, monto: r2(base + igv) }))
  }

  const handleMontoDirectChange = (val: number) => {
    setForm(f => ({ ...f, monto: r2(val), base_imponible: null, igv: null }))
  }

  const handleDetraccionToggle = (checked: boolean) => {
    setForm(f => ({
      ...f,
      tiene_detraccion: checked,
      porcentaje_detraccion: checked ? (f.porcentaje_detraccion ?? 12) : null,
      monto_detraccion: checked ? r2(f.monto * (f.porcentaje_detraccion ?? 12) / 100) : null,
    }))
  }

  const handleDetraccionPctChange = (pct: number) => {
    setForm(f => ({
      ...f,
      porcentaje_detraccion: pct,
      monto_detraccion: r2(f.monto * pct / 100),
    }))
  }

  /* ─── Upload comprobante ─── */
  const handleUploadComprobante = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const ts = Date.now()
      const path = `temp/${ts}_${file.name}`
      const { error } = await supabase.storage.from('comprobantes-pago').upload(path, file)
      if (error) throw error
      const { data: urlData } = supabase.storage.from('comprobantes-pago').getPublicUrl(path)
      const comp: ComprobanteMovimiento = {
        nombre: file.name,
        url: urlData.publicUrl,
        tipo: file.type,
        subido_at: new Date().toISOString(),
      }
      setForm(prev => ({ ...prev, comprobantes: [...prev.comprobantes, comp] }))
      showToast('Comprobante subido', 'success')
    } catch {
      showToast('Error al subir comprobante', 'error')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const removeComprobante = (idx: number) => {
    setForm(prev => ({ ...prev, comprobantes: prev.comprobantes.filter((_, i) => i !== idx) }))
  }

  /* ─── CRUD ─── */
  const openCreate = () => {
    setForm({ ...emptyMov, fecha: new Date().toISOString().slice(0, 10) })
    setEditId(null)
    setModal('create')
  }
  const openEdit = (m: MovimientoCaja) => {
    setForm({
      tipo: m.tipo, categoria: m.categoria, descripcion: m.descripcion, monto: m.monto,
      moneda: m.moneda, fecha: m.fecha, factura_id: m.factura_id, concepto: m.concepto,
      pago_programado_id: m.pago_programado_id, cliente_id: m.cliente_id,
      comprobantes: m.comprobantes || [], notas: m.notas,
      base_imponible: m.base_imponible, igv: m.igv,
      serie_comprobante: m.serie_comprobante, numero_comprobante: m.numero_comprobante,
      tipo_comprobante: m.tipo_comprobante,
      tiene_detraccion: m.tiene_detraccion ?? false,
      porcentaje_detraccion: m.porcentaje_detraccion,
      monto_detraccion: m.monto_detraccion,
    })
    setEditId(m.id)
    setModal('edit')
  }

  const handleSave = async () => {
    if (!form.descripcion.trim()) { showToast('Descripción requerida', 'error'); return }
    if (form.monto <= 0) { showToast('Monto debe ser mayor a 0', 'error'); return }
    try {
      if (modal === 'edit' && editId) {
        await updateMut.mutateAsync({ id: editId, ...form } as never)
        showToast('Movimiento actualizado', 'success')
      } else {
        await addMut.mutateAsync(form as never)
        showToast('Movimiento registrado', 'success')
      }
      setModal(null)
    } catch {
      showToast('Error al guardar', 'error')
    }
  }

  const handleDelete = async () => {
    if (!del) return
    try {
      await removeMut.mutateAsync(del.id)
      showToast('Movimiento eliminado', 'success')
      setDel(null)
    } catch {
      showToast('Error al eliminar', 'error')
    }
  }

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  /* ─── Franja de KPIs por moneda ──────────────────────────────────────────
     Mezclar S/ y $ en un total unico seria un numero inventado: una fila
     por moneda, y la fila no se dibuja si esa moneda no tuvo movimiento. */
  const renderKpiRow = (moneda: string, t: typeof totals.pen) => {
    if (t.ingresos === 0 && t.egresos === 0) return null
    const sym = moneda === 'USD' ? '$' : 'S/'
    return (
      <KpiRow moneda={moneda} key={moneda}>
        <StatCard label="Ingresos" value={formatCurrency(t.ingresos, moneda)} tone="success" />
        <StatCard
          label="Base imp."
          value={<>{formatCurrency(t.base_ing, moneda)}<div className="kpi-sub">IGV: {sym}{t.igv_ing.toFixed(2)}</div></>}
        />
        <StatCard label="Egresos" value={formatCurrency(t.egresos, moneda)} tone="danger" />
        <StatCard
          label="Base egr."
          value={<>{formatCurrency(t.base_egr, moneda)}<div className="kpi-sub">IGV: {sym}{t.igv_egr.toFixed(2)}</div></>}
        />
        <StatCard label="Saldo" value={formatCurrency(t.saldo, moneda)} tone={t.saldo >= 0 ? 'success' : 'danger'} />
      </KpiRow>
    )
  }

  /* ─── Exportar a Excel/CSV ───────────────────────────────────────────────
     El contador necesita bajar el libro para cruzarlo con el suyo. Se exporta
     lo que esta filtrado en pantalla, no toda la tabla, para que el recorte
     (ano/mes/tipo/categoria) sea el mismo que esta viendo.
     El formato (separador, BOM, CRLF) lo resuelve `@/lib/csv`. */
  const exportarCsv = () => {
    if (filtered.length === 0) { showToast('No hay movimientos que exportar', 'error'); return }

    const cols: CsvColumn<MovimientoCaja>[] = [
      ['Fecha', m => m.fecha],
      ['Tipo', m => (m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso')],
      ['Categoria', m => m.categoria || ''],
      ['Tipo Comprobante', m => (m.tipo_comprobante ? TIPO_COMPROBANTE_LABELS[m.tipo_comprobante] || m.tipo_comprobante : '')],
      ['Serie', m => m.serie_comprobante || ''],
      ['Numero', m => m.numero_comprobante || ''],
      ['Descripcion', m => m.descripcion || ''],
      ['Moneda', m => m.moneda || 'PEN'],
      ['Base Imponible', m => (m.base_imponible ?? '')],
      ['IGV', m => (m.igv ?? '')],
      // Firmado: el egreso sale negativo para que la suma en Excel de el saldo.
      ['Total', m => (m.tipo === 'egreso' ? -Number(m.monto) : Number(m.monto))],
      ['Detraccion %', m => (m.tiene_detraccion ? (m.porcentaje_detraccion ?? '') : '')],
      ['Monto Detraccion', m => (m.tiene_detraccion ? (m.monto_detraccion ?? '') : '')],
      ['Cliente', m => (m.cliente_id ? clientes.find(c => c.id === m.cliente_id)?.empresa || '' : '')],
      ['Notas', m => m.notas || ''],
      ['Comprobantes', m => (m.comprobantes || []).map(c => c.url).join(' | ')],
    ]

    const sufijo = [
      filtroYear !== 'todos' ? filtroYear : 'todos',
      filtroMonth !== 'todos' ? MONTH_NAMES[Number(filtroMonth) - 1] : null,
      filtroTipo !== 'todos' ? filtroTipo : null,
      filtroCategoria !== 'todos' ? filtroCategoria.toLowerCase().replace(/\s+/g, '-') : null,
    ].filter(Boolean).join('-')

    descargarCsv(`flujo-caja-${sufijo}`, cols, filtered)
    showToast(`${filtered.length} movimiento${filtered.length !== 1 ? 's' : ''} exportado${filtered.length !== 1 ? 's' : ''}`, 'success')
  }

  return (
    <div className="page-enter page-shell" id="flujo-caja">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === items.length
          ? `${items.length} movimiento${items.length !== 1 ? 's' : ''}`
          : `${total} de ${items.length}`}
        actions={
          <>
            <button className="btn btn-secondary" onClick={exportarCsv} id="btn-export-caja" title="Descarga en CSV lo que esta filtrado en pantalla">
              <IconDownload size={15} /> Exportar
            </button>
            <button className="btn btn-primary" onClick={openCreate} id="btn-new-movimiento">
              <IconPlus size={16} /> Nuevo Movimiento
            </button>
          </>
        }
      />

      <KpiStrip>
        {renderKpiRow('USD', totals.usd)}
        {renderKpiRow('PEN', totals.pen)}
      </KpiStrip>

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por descripción, categoría, serie..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroYear}
          onChange={e => { setFiltroYear(e.target.value); if (e.target.value === 'todos') setFiltroMonth('todos') }}
          style={{ width: 100 }}>
          <option value="todos">Año</option>
          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="form-input" value={filtroMonth} onChange={e => setFiltroMonth(e.target.value)}
          style={{ width: 105 }} disabled={filtroYear === 'todos'}>
          <option value="todos">Mes</option>
          {MONTH_NAMES.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
        </select>
        <select className="form-input" value={filtroTipo}
          onChange={e => setFiltroTipo(e.target.value as 'todos' | 'ingreso' | 'egreso')} style={{ width: 140 }}>
          <option value="todos">Ingresos y egresos</option>
          <option value="ingreso">Solo ingresos</option>
          <option value="egreso">Solo egresos</option>
        </select>
        <select className="form-input" value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} style={{ width: 195 }}>
          <option value="todos">Todas las categorías</option>
          {categorias.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <h3>Sin movimientos</h3>
          <p>Registra tu primer movimiento de caja.</p>
          <button className="btn btn-primary" onClick={openCreate}>Registrar Movimiento</button>
        </div>
      ) : (
        <>
        <div className="table-container page-shell__table">
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Comprobante</th>
                <th>Categoría</th>
                <th>Descripción</th>
                <th style={{ textAlign: 'right' }}>Base</th>
                <th style={{ textAlign: 'right' }}>IGV</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th>Adjuntos</th>
                <th style={{ width: 100 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (

                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    Sin resultados para el filtro aplicado
                  </td>
                </tr>
              ) : paginatedItems.map(m => (
                <tr key={m.id}>
                  <td className="cell-date">{formatDate(m.fecha)}</td>
                  <td>
                    <span className={`badge ${m.tipo === 'ingreso' ? 'badge-ingreso' : 'badge-egreso'}`}>
                      {m.tipo === 'ingreso' ? <IconArrowUp size={11} /> : <IconArrowDown size={11} />}
                      {m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {(m.serie_comprobante || m.numero_comprobante) ? (
                      <span title={m.tipo_comprobante ? TIPO_COMPROBANTE_LABELS[m.tipo_comprobante] : undefined}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <TipoComprobanteIcon tipo={m.tipo_comprobante} />
                        {/* Un pago sin serie (operacion bancaria, NPS) igual tiene
                            numero: exigir serie lo dejaba invisible en la tabla. */}
                        {[m.serie_comprobante, m.numero_comprobante].filter(Boolean).join('-')}
                      </span>
                    ) : (
                      <span className="caja-sin-comprobante">—</span>
                    )}
                  </td>
                  <td>
                    {m.categoria
                      ? <span className="badge badge-categoria">{m.categoria}</span>
                      : <span className="caja-sin-comprobante">—</span>}
                  </td>
                  <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.descripcion || '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    {m.base_imponible != null ? formatCurrency(m.base_imponible, m.moneda) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    {m.igv != null ? formatCurrency(m.igv, m.moneda) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: m.tipo === 'ingreso' ? 'var(--success)' : 'var(--danger)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                      <span>{m.tipo === 'ingreso' ? '+' : '-'}{formatCurrency(m.monto, m.moneda)}</span>
                      {m.tiene_detraccion && (
                        <span className="badge" style={{ background: 'color-mix(in srgb, var(--warning) 15%, transparent)', color: 'var(--warning)', fontSize: '0.6rem', padding: '1px 5px', fontWeight: 700 }}>
                          SPOT
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {(m.comprobantes?.length || 0) > 0 ? (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {m.comprobantes.map((c, i) => (
                          <a key={i} href={c.url} target="_blank" rel="noreferrer" title={c.nombre}
                            className="badge badge-adjunto">
                            {/* Antes el badge decia "1"/"2": un indice sin significado.
                                El formato al menos distingue el PDF de la foto. */}
                            {c.tipo?.includes('pdf')
                              ? <><IconFileText size={11} /> PDF</>
                              : c.tipo?.startsWith('image/')
                                ? <><IconImage size={11} /> IMG</>
                                : <><IconPaperclip size={11} /> Archivo</>}
                          </a>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td>
                    <div className="row-actions row-actions--nowrap">
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(m)} title="Editar"><IconEdit size={13} /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => setDel(m)} title="Eliminar"><IconTrash size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
          onPage={setPage} noun="movimientos" />
        </>
      )}

      {/* ─── Modal Crear/Editar ─── */}
      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={modal === 'edit' ? 'Editar Movimiento' : 'Nuevo Movimiento'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Tipo</label>
              <select className="form-input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value as 'ingreso' | 'egreso' }))}>
                <option value="egreso">Egreso</option>
                <option value="ingreso">Ingreso</option>
              </select>
            </div>
            <div>
              <label className="form-label">Fecha</label>
              <input type="date" className="form-input" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="form-label">Categoría</label>
            <input className="form-input" value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} list="categorias-list" placeholder="Ej: Alquiler, Nómina, Pago factura..." />
            <datalist id="categorias-list">
              {categorias.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <label className="form-label">Descripción *</label>
            <input className="form-input" value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Descripción del movimiento" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Monto total *</label>
              <input type="number" className="form-input" value={form.monto || ''} onChange={e => handleMontoDirectChange(parseFloat(e.target.value) || 0)} min="0" step="0.01" />
            </div>
            <div>
              <label className="form-label">Moneda</label>
              <select className="form-input" value={form.moneda} onChange={e => setForm(f => ({ ...f, moneda: e.target.value }))}>
                <option value="PEN">PEN</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>

          {/* ─── Desglose Tributario ─── */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
            <label className="form-label" style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8, display: 'block' }}>
              <IconList size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Desglose Tributario
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="form-label">Tipo de Comprobante</label>
                <select className="form-input" value={form.tipo_comprobante ?? ''} onChange={e => setForm(f => ({ ...f, tipo_comprobante: (e.target.value || null) as TipoComprobante | null }))}>
                  <option value="">— Sin comprobante —</option>
                  <option value="factura">Factura</option>
                  <option value="boleta">Boleta</option>
                  <option value="recibo">Recibo por Honorarios</option>
                  <option value="nota_credito">Nota de Crédito</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label className="form-label">Serie</label>
                  <input className="form-input" value={form.serie_comprobante || ''} onChange={e => setForm(f => ({ ...f, serie_comprobante: e.target.value || null }))} placeholder="E001" style={{ fontFamily: 'monospace' }} />
                </div>
                <div>
                  <label className="form-label">Número</label>
                  <input className="form-input" value={form.numero_comprobante || ''} onChange={e => setForm(f => ({ ...f, numero_comprobante: e.target.value || null }))} placeholder="146" style={{ fontFamily: 'monospace' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <label className="form-label">Base Imponible</label>
                <input type="number" className="form-input" value={form.base_imponible ?? ''} onChange={e => handleBaseChange(parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="0.00" />
              </div>
              <div>
                <label className="form-label">IGV (18%)</label>
                <input type="number" className="form-input" value={form.igv ?? ''} onChange={e => handleIgvChange(parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="Auto" />
              </div>
              <div>
                <label className="form-label">Total (monto)</label>
                <input type="number" className="form-input" value={form.monto || ''} readOnly style={{ background: 'var(--surface)', fontWeight: 700 }} />
              </div>
            </div>
          </div>

          {/* ─── Detracción SPOT ─── */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
              <input type="checkbox" checked={form.tiene_detraccion} onChange={e => handleDetraccionToggle(e.target.checked)} />
              ¿Aplica detracción SPOT?
            </label>
            {form.tiene_detraccion && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                <div>
                  <label className="form-label">Porcentaje (%)</label>
                  <input type="number" className="form-input" value={form.porcentaje_detraccion ?? 12} onChange={e => handleDetraccionPctChange(parseFloat(e.target.value) || 0)} min="0" max="100" step="0.5" />
                </div>
                <div>
                  <label className="form-label">Monto detracción</label>
                  <input type="number" className="form-input" value={form.monto_detraccion ?? ''} readOnly style={{ background: 'var(--surface)', fontWeight: 700 }} />
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Factura vinculada</label>
              <select className="form-input" value={form.factura_id ?? ''} onChange={e => setForm(f => ({ ...f, factura_id: e.target.value ? parseInt(e.target.value) : null }))}>
                <option value="">— Ninguna —</option>
                {facturas.map(f => <option key={f.id} value={f.id}>{f.numero} — {f.cliente_nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Cliente</label>
              <select className="form-input" value={form.cliente_id ?? ''} onChange={e => setForm(f => ({ ...f, cliente_id: e.target.value ? parseInt(e.target.value) : null }))}>
                <option value="">— Ninguno —</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
              </select>
            </div>
          </div>

          {/* Comprobantes */}
          <div>
            <label className="form-label">Comprobantes</label>
            {form.comprobantes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                {form.comprobantes.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', background: 'var(--surface)', borderRadius: 6, fontSize: '0.8rem' }}>
                    <a href={c.url} target="_blank" rel="noreferrer" className="cell-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><IconPaperclip size={12} /> {c.nombre}</a>
                    <button className="btn btn-sm btn-danger" onClick={() => removeComprobante(i)} style={{ padding: '2px 6px' }} title="Quitar"><IconX size={12} /></button>
                  </div>
                ))}
              </div>
            )}
            <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {uploading ? <span className="auth-spinner" style={{ width: 14, height: 14 }} /> : <IconPaperclip size={13} />} Subir comprobante
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleUploadComprobante} style={{ display: 'none' }} />
            </label>
          </div>

          <div>
            <label className="form-label">Notas</label>
            <textarea className="form-input" value={form.notas || ''} onChange={e => setForm(f => ({ ...f, notas: e.target.value || null }))} rows={2} />
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={addMut.isPending || updateMut.isPending}>
            {(addMut.isPending || updateMut.isPending) ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </Modal>

      {/* ─── Modal Eliminar ─── */}
      <Modal isOpen={!!del} onClose={() => setDel(null)} title="Eliminar Movimiento">
        <p>¿Eliminar movimiento <strong>{del?.descripcion}</strong>?</p>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setDel(null)}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleDelete}>Eliminar</button>
        </div>
      </Modal>
    </div>
  )
}
